import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDeleteModal } from "../../../../hooks/shared/useDeleteModal";
import { filterRowsByQuery } from "../../../../utils/shared/searchHelpers";
import { formatValidationErrors } from "../../utils/adminApiHelpers";
import {
  buildReferenceFormData,
  buildReferenceFormDataForUpdate,
  createReference,
  deleteReference,
  fetchReferenceById,
  fetchReferenceList,
  unwrapList,
  unwrapOne,
  updateReference,
} from "../api";
import { EMPTY_REFERENCE_FORM } from "../constants/emptyDefaults";
import { getReferenceColumns } from "../constants/referenceColumns";
import { getReferenceFields } from "../constants/referenceFields";
import { normalizeReferenceItem } from "../utils/normalizeReference";

export function useManageReferences() {
  const { t } = useTranslation("dashboard");
  const tk = useCallback((key) => t(`manageReferences.${key}`), [t]);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const deleteModal = useDeleteModal();
  const [editingItem, setEditingItem] = useState(null);
  const [newFormKey, setNewFormKey] = useState(0);
  const [submitError, setSubmitError] = useState(null);

  const editingId = editingItem?.id;

  const { data: editingDetail, isFetching: isEditLoading } = useQuery({
    queryKey: ["admin", "references", "detail", editingId],
    queryFn: async () => {
      const res = await fetchReferenceById(editingId);
      const raw = unwrapOne(res);
      return (
        normalizeReferenceItem(raw) ?? normalizeReferenceItem(raw?.data)
      );
    },
    enabled: editingId != null,
  });

  const formDefaults = useMemo(() => {
    if (!editingItem) return { ...EMPTY_REFERENCE_FORM };
    const s = editingDetail ?? editingItem;
    return {
      title_ar: s.title_ar ?? "",
      title_en: s.title_en ?? "",
      description_ar: s.description_ar ?? "",
      description_en: s.description_en ?? "",
    };
  }, [editingItem, editingDetail]);

  const fields = useMemo(() => getReferenceFields(tk), [tk]);
  const columns = useMemo(() => getReferenceColumns(tk), [tk]);

  const {
    data: rows = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin", "references"],
    queryFn: async () => {
      const res = await fetchReferenceList();
      return unwrapList(res).map(normalizeReferenceItem).filter(Boolean);
    },
  });

  const createMutation = useMutation({
    mutationFn: (fd) => createReference(fd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "references"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) => updateReference(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "references"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteReference(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "references"] });
    },
  });

  const handleSubmit = async (data) => {
    setSubmitError(null);
    const fd = editingItem
      ? buildReferenceFormDataForUpdate(data)
      : buildReferenceFormData(data);
    try {
      if (editingItem) {
        await updateMutation.mutateAsync({ id: editingItem.id, formData: fd });
        setEditingItem(null);
      } else {
        await createMutation.mutateAsync(fd);
      }
      setNewFormKey((k) => k + 1);
    } catch (err) {
      setSubmitError(formatValidationErrors(err));
    }
  };

  const confirmDelete = async () => {
    if (deleteModal.deleteId == null) return;
    try {
      await deleteMutation.mutateAsync(deleteModal.deleteId);
    } finally {
      deleteModal.close();
    }
  };

  const filtered = useMemo(
    () => filterRowsByQuery(rows, search, ["title_en", "title_ar"]),
    [rows, search]
  );

  const errorMessage =
    error?.response?.data?.message ||
    error?.message ||
    (isError ? tk("alerts.loadFailed") : null);

  const pending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return {
    tk,
    search,
    setSearch,
    deleteModal,
    editingItem,
    newFormKey,
    submitError,
    isEditLoading,
    formDefaults,
    fields,
    columns,
    filtered,
    isLoading,
    errorMessage,
    pending,
    handleSubmit,
    confirmDelete,
    handleEdit: setEditingItem,
    handleDeleteClick: deleteModal.open,
    cancelEdit: () => setEditingItem(null),
  };
}
